import cn from 'classnames';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './styles.module.scss';

export interface TabsProps {
  className?: string;
}

const Tabs: React.FC<TabsProps> = ({ className, ...rest }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isEnhanced, setIsEnhanced] = useState(false);

  const activateTab = useCallback((tabButton: HTMLButtonElement) => {
    const tabList = tabButton.closest('[role="tablist"]');
    if (!tabList) return;

    const tabButtons = Array.from(tabList.querySelectorAll('button[role="tab"]'));
    tabButtons.forEach(button => {
      button.setAttribute('aria-selected', button === tabButton ? 'true' : 'false');
      button.setAttribute('tabIndex', button === tabButton ? '0' : '-1');
    });

    const tabsRoot = ref.current;
    if (!tabsRoot) return;

    const tabPanels = Array.from(tabsRoot.querySelectorAll<HTMLElement>('[role="tabpanel"]'));
    tabPanels.forEach(panel => {
      panel.removeAttribute('aria-hidden');
      panel.removeAttribute('hidden');
      panel.setAttribute('data-tab-active', 'false');
      panel.classList.remove('active');
      panel.setAttribute('tabIndex', '-1');
    });

    const tabPanelId = tabButton.getAttribute('aria-controls');
    if (!tabPanelId) return;

    const tabPanel = tabsRoot.querySelector<HTMLElement>(`#${tabPanelId}`);
    if (!tabPanel) return;

    tabPanel.setAttribute('data-tab-active', 'true');
    tabPanel.classList.add('active');
    tabPanel.setAttribute('tabIndex', '0');
  }, []);

  useEffect(() => {
    setIsEnhanced(true);

    const tabsRoot = ref.current;
    if (!tabsRoot) return;

    const tabLists = Array.from(tabsRoot.querySelectorAll<HTMLElement>('[role="tablist"]'));

    // Remove server-provided hidden semantics so content remains discoverable in Reader mode.
    const tabPanels = Array.from(tabsRoot.querySelectorAll<HTMLElement>('[role="tabpanel"]'));
    tabPanels.forEach(panel => {
      panel.removeAttribute('aria-hidden');
      panel.removeAttribute('hidden');
      panel.setAttribute('data-tab-active', 'false');
    });

    tabLists.forEach(tabList => {
      const tabButtons = Array.from(tabList.querySelectorAll<HTMLButtonElement>('button[role="tab"]'));
      if (tabButtons.length === 0) return;

      const selectedButton =
        tabButtons.find(button => button.getAttribute('aria-selected') === 'true') ?? tabButtons[0];

      activateTab(selectedButton);
    });
  }, [activateTab]);

  const handleClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const tabButton = (event.target as HTMLElement).closest('button[role="tab"]') as HTMLButtonElement | null;
      if (tabButton) {
        activateTab(tabButton);
      }
    },
    [activateTab]
  );

  const handleKeyDownCapture = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const tabButton = (event.target as HTMLElement).closest('button[role="tab"]') as HTMLButtonElement | null;
    if (tabButton && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      const tabButtons = Array.from(
        tabButton.closest('[role="tablist"]')?.querySelectorAll('button[role="tab"]') || []
      );
      const currentIndex = tabButtons.indexOf(tabButton);
      let newIndex = currentIndex;

      if (event.key === 'ArrowLeft') {
        newIndex = currentIndex === 0 ? tabButtons.length - 1 : currentIndex - 1;
      } else if (event.key === 'ArrowRight') {
        newIndex = currentIndex === tabButtons.length - 1 ? 0 : currentIndex + 1;
      }

      const newTabButton = tabButtons[newIndex] as HTMLButtonElement;
      newTabButton.focus();
      newTabButton.click();
    }
    if (tabButton && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault();
      const tabButtons = Array.from(
        tabButton.closest('[role="tablist"]')?.querySelectorAll('button[role="tab"]') || []
      );
      let newIndex = 0;

      if (event.key === 'End') {
        newIndex = tabButtons.length - 1;
      }

      const newTabButton = tabButtons[newIndex] as HTMLButtonElement;
      newTabButton.focus();
      newTabButton.click();
    }
  }, []);

  return (
    <div
      ref={ref}
      data-enhanced={isEnhanced ? 'true' : undefined}
      onClickCapture={handleClickCapture}
      onKeyDownCapture={handleKeyDownCapture}
      className={cn(styles.tabs, className)}
      {...rest}
    />
  );
};

export default Tabs;
